<template>
  <div class="dash">
    <header class="dash__header">
      <div class="dash__headtext">
        <div class="dash__eyebrow">Hoy · {{ todayLabel }}</div>
        <h1 class="dash__title">Para hoy</h1>
        <p class="dash__sub">Lo que necesitás accionar, ordenado por urgencia.</p>
      </div>
      <div v-if="isAdmin" class="dash__scope">
        <button
          v-for="s in scopes"
          :key="s.value"
          type="button"
          class="dash__scopebtn"
          :class="{ 'dash__scopebtn--on': scope === s.value }"
          @click="setScope(s.value)"
        >{{ s.label }}</button>
      </div>
    </header>

    <div v-if="loading" class="dash__loading">
      <v-progress-circular indeterminate color="accent" size="34" width="3" />
    </div>

    <template v-else>
      <div v-if="isAdmin && data.expiredCount" class="dash__expired">
        <span class="dash__expiredn">{{ data.expiredCount }}</span>
        consulta{{ data.expiredCount === 1 ? "" : "s" }} venció sin respuesta (+2 semanas) — fuera del feed; revisalas en
        <router-link :to="{ name: 'Consultas' }" class="dash__expiredlink">Consultas</router-link>.
      </div>

      <pulse-tiles :pulse="data.pulse" :unanswered="data.unansweredCount || 0"></pulse-tiles>

      <live-widget v-if="isAdmin"></live-widget>

      <!-- the hottest engaged leads lead at full width; the next two pair up -->
      <action-queue
        horizon="Ahora"
        title="Leads calientes"
        :items="data.hotOpen"
        accent="hot"
        empty="No hay leads calientes con consultas abiertas."
        @open-inquiry="openInquiry"
        @open-journey="openJourney"
      ></action-queue>

      <div class="dash__pair">
        <action-queue
          horizon="Hoy"
          title="Sin responder"
          :items="data.unanswered"
          :count="data.unansweredCount"
          accent="brand"
          empty="¡Todo respondido! No hay consultas pendientes."
          @open-inquiry="openInquiry"
          @open-journey="openJourney"
        ></action-queue>
        <action-queue
          horizon="Esta semana"
          title="Se enfriaron"
          :items="data.stale"
          accent="cold"
          empty="Ningún lead abierto se enfrió. ¡Buen ritmo!"
          @open-inquiry="openInquiry"
          @open-journey="openJourney"
        ></action-queue>
      </div>

      <property-attention
        :items="data.hotProperties"
        @open-inquiries="openInquiries"
        @open-property="openProperty"
      ></property-attention>

      <section v-if="isAdmin" class="dash__team">
        <div class="dash__teamlabel">Equipo y mercado</div>

        <action-queue
          horizon="Sin asignar"
          title="Consultas sin dueño"
          :items="data.unassigned"
          accent="brand"
          empty="No hay consultas sin asignar."
          @open-inquiry="openInquiry"
          @open-journey="openJourney"
        ></action-queue>

        <div class="dash__pair">
          <pipeline-bar :pipeline="data.pipeline"></pipeline-bar>
          <agent-workload :items="data.agentWorkload"></agent-workload>
        </div>

        <market-health></market-health>
      </section>
    </template>

    <inquiry-detail-dialog ref="inquiryDetail" @reload="fetch"></inquiry-detail-dialog>
    <inquiries-dialog
      ref="inquiriesDialog"
      @open-property="openProperty"
      @open-journey="openJourney"
    ></inquiries-dialog>
    <visitor-journey-dialog
      ref="journey"
      @open-inquiry="openInquiry"
      @open-property="openProperty"
    ></visitor-journey-dialog>
    <property-detail ref="detail"></property-detail>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { SalesDashboard } from "@/graphql/queries/dashboard";
import ActionQueue from "@/components/dashboard/ActionQueue";
import PulseTiles from "@/components/dashboard/PulseTiles";
import LiveWidget from "@/components/dashboard/LiveWidget";
import PipelineBar from "@/components/dashboard/PipelineBar";
import PropertyAttention from "@/components/dashboard/PropertyAttention";
import AgentWorkload from "@/components/dashboard/AgentWorkload";
import MarketHealth from "@/components/dashboard/MarketHealth";
import InquiryDetailDialog from "@/components/inquiries/InquiryDetailDialog";
import InquiriesDialog from "@/components/properties/InquiriesDialog";
import VisitorJourneyDialog from "@/components/reports/VisitorJourneyDialog";
import PropertyDetail from "@/components/properties/Detail";

export default {
  components: {
    ActionQueue,
    PulseTiles,
    LiveWidget,
    PipelineBar,
    PropertyAttention,
    AgentWorkload,
    MarketHealth,
    InquiryDetailDialog,
    InquiriesDialog,
    VisitorJourneyDialog,
    PropertyDetail
  },

  data: () => ({
    loading: false,
    data: {},
    scope: "global",
    scopes: [
      { value: "global", label: "Global" },
      { value: "managed", label: "Mis propiedades" }
    ]
  }),

  computed: {
    ...mapGetters(["currentUser"]),

    isAdmin() {
      const roles = (this.currentUser && this.currentUser.displayRoles) || [];
      return roles.includes("gendev") || roles.includes("admin");
    },

    todayLabel() {
      try {
        const s = new Date().toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
        return s.charAt(0).toUpperCase() + s.slice(1);
      } catch (e) {
        return "";
      }
    }
  },

  watch: {
    // Wait until the role is known before fetching, so an admin lands on a SELECTED
    // scope ("Global") rather than an ambiguous toggle with nothing active. (currentUser
    // can resolve after created(), which is why we key off it instead of created().)
    currentUser: {
      immediate: true,
      handler(user) {
        if (!user) return;
        this.scope = this.isAdmin ? "global" : "mine";
        this.fetch();
      }
    }
  },

  methods: {
    setScope(value) {
      if (this.scope === value) return;
      this.scope = value;
      this.fetch();
    },

    fetch() {
      this.loading = true;
      this.$apollo
        .query({ query: SalesDashboard, fetchPolicy: "no-cache", variables: { scope: this.scope } })
        .then(res => {
          this.data = res.data.salesDashboard || {};
        })
        .finally(() => {
          this.loading = false;
        });
    },

    openInquiry(id) {
      this.$refs.inquiryDetail.open(id);
    },
    openJourney(visitorId) {
      this.$refs.journey.open(visitorId);
    },
    openProperty(property) {
      this.$refs.detail.open(property);
    },
    openInquiries(property) {
      this.$refs.inquiriesDialog.open(property);
    }
  }
};
</script>

<style scoped>
.dash { background: #f7f5f1; min-height: 100%; max-width: 1180px; }

/* header — a daily-briefing masthead */
.dash__header { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 22px; }
@media (max-width: 600px) { .dash__header { flex-direction: column; align-items: stretch; } }
.dash__eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: #b52118; margin-bottom: 7px; }
.dash__title { font-size: 28px; font-weight: 700; line-height: 1.05; color: #1a1814; margin: 0; }
.dash__sub { font-size: 13.5px; color: #756f64; margin: 5px 0 0; }

.dash__scope { display: inline-flex; background: #f1ece3; border-radius: 9px; padding: 3px; gap: 2px; align-self: flex-end; }
.dash__scopebtn {
  border: none; background: transparent; cursor: pointer; font: inherit; font-size: 13px; font-weight: 600;
  color: #6a645a; border-radius: 7px; padding: 7px 14px; transition: background 0.15s, color 0.15s;
}
.dash__scopebtn--on { background: #fff; color: #1a1814; font-weight: 700; box-shadow: 0 1px 4px -1px rgba(40, 33, 22, 0.3); }

.dash__loading { display: flex; justify-content: center; padding: 64px 0; }

.dash__expired {
  display: flex; align-items: center; flex-wrap: wrap; gap: 5px;
  background: #fdf3e7; border: 1px solid #f1ddae; border-radius: 12px;
  padding: 11px 15px; margin-bottom: 18px; font-size: 13px; color: #8a5a00;
}
.dash__expiredn { font-weight: 700; }
.dash__expiredlink { color: #b52118; font-weight: 600; text-decoration: none; }
.dash__expiredlink:hover { text-decoration: underline; }

/* paired columns — secondary queues / manager widgets side by side; collapse early */
.dash__pair { display: grid; grid-template-columns: 1fr 1fr; gap: 0 22px; }
@media (max-width: 860px) { .dash__pair { grid-template-columns: 1fr; } }

/* manager block */
.dash__team { border-top: 1px solid #ece7de; margin-top: 4px; padding-top: 22px; }
.dash__teamlabel { font-size: 10px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: #a39a8c; margin-bottom: 16px; }
</style>
